import React, { useEffect, useCallback } from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { ReportNames } from '../../reports';
import { useHistory } from 'react-router';
import { UsageApi } from '../../../../../services/api';
import ApplicationRoutes from '../../../../../constants/routes';
import { useTranslation } from 'react-i18next';
import { applyPartialDateRangeRules } from '../../../../../utils/calendars';
import ReportFilterConstants from './../../../../../constants/reports/filters';

interface Props {
  checkoutsReportFilters: ReportFiltersWithIncrement;
}

const LicenseCheckoutsFilter: React.FC<ReportChartFilterProps & Props> = ({
  reportFilters,
  checkoutsReportFilters,
  setSpecificReportFilters,
  setReportDataSource,
  setCalendarType,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const updateReportDataSource = useCallback((): void => {
    setReportDataSource(ReportNames.Checkouts, () => {
      switch (checkoutsReportFilters.increment) {
        case ReportFilterConstants.FILTERINCREMENT_DAY: {
          return UsageApi.getUsageByDay(
            reportFilters.fromDate,
            reportFilters.toDate,
            reportFilters.client,
            reportFilters.product &&
              reportFilters.product.id === ReportFilterConstants.PRODUCTOPTION_ALL_PRODUCTS
              ? null
              : reportFilters.product,
            'total',
          );
        }

        case ReportFilterConstants.FILTERINCREMENT_WEEK: {
          return UsageApi.getUsageByWeek(
            applyPartialDateRangeRules(reportFilters.fromDate, reportFilters.calendarType, true),
            applyPartialDateRangeRules(reportFilters.toDate, reportFilters.calendarType, false),
            reportFilters.client,
            reportFilters.product &&
              reportFilters.product.id === ReportFilterConstants.PRODUCTOPTION_ALL_PRODUCTS
              ? null
              : reportFilters.product,
            'total',
          );
        }

        case ReportFilterConstants.FILTERINCREMENT_MONTH: {
          return UsageApi.getUsageByMonth(
            applyPartialDateRangeRules(reportFilters.fromDate, reportFilters.calendarType, true),
            applyPartialDateRangeRules(reportFilters.toDate, reportFilters.calendarType, false),
            reportFilters.client,
            reportFilters.product &&
              reportFilters.product.id === ReportFilterConstants.PRODUCTOPTION_ALL_PRODUCTS
              ? null
              : reportFilters.product,
            'total',
          );
        }

        default: {
          // NOTE:  This is based on a fixed dropdown, so this case should never be hit. Returning
          // an empty promise to satisfy the return type, but the redirect will run prior.
          history.push(ApplicationRoutes.ERROR_ERROR);
          return new Promise<BTChartData>((): void => {});
        }
      }
    });
  }, [setReportDataSource, checkoutsReportFilters, reportFilters, history]);

  const updateReportFilterValues = useCallback(
    (checkoutsReportFilters: ReportFiltersWithIncrement): void => {
      setSpecificReportFilters(ReportNames.Checkouts, checkoutsReportFilters);
      setCalendarType(checkoutsReportFilters.increment);
      updateReportDataSource();
    },
    [setSpecificReportFilters, setCalendarType, updateReportDataSource],
  );

  // Set the data source values for this component when the generic report filters provided to this component change
  useEffect(() => {
    updateReportDataSource();
  }, [reportFilters, updateReportDataSource]);

  // If the checkouts report filters are not initialized, initialize them
  useEffect(() => {
    if (!checkoutsReportFilters) {
      updateReportFilterValues({
        increment: ReportFilterConstants.FILTERINCREMENT_DAY,
      });
    }
  }, [updateReportFilterValues, checkoutsReportFilters]);

  return (
    <div className="row" data-testid="reports-page-license-checkouts-chart-filters">
      <div className="col-12 col-md-3 mt-3">
        <label htmlFor="reportIntervalDropdown">{t('reporting:filters.reportInterval')}</label>
        <DropDownList
          id="reportIntervalDropdown"
          data={ReportFilterConstants.FILTERINCREMENT_DEFAULT_INCREMENTS}
          onChange={(event): void => {
            updateReportFilterValues({
              ...checkoutsReportFilters,
              increment: event.target.value,
            });
          }}
          value={(checkoutsReportFilters && checkoutsReportFilters.increment) || ''}
        />
      </div>
    </div>
  );
};

export default LicenseCheckoutsFilter;
